import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Users, MapPin, Home, Waves, Car, Ship, Utensils, Wifi, Heart, Menu, X, BedDouble } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO, { LodgingSchema } from "@/components/SEO";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Floor plan image URL (converted from PDF)
const FLOOR_PLAN_IMAGE = "https://customer-assets.emergentagent.com/job_eventlodgeapp/artifacts/03trf8wy_209%20Upstairs.pdf";

const amenityIcons = {
  "Short walk to 3 beaches": Waves,
  "Steps to sandy beaches": Waves,
  "Full commercial kitchen": Utensils,
  "Ferry shuttle available": Ship,
  "Ferry shuttle service": Ship,
  "Private en-suites": Home,
  "Private ensuites": Home,
  "Historic charm": Heart,
  "Wrap-around porches": Home,
  "Outdoor gathering spaces": Users,
  "WiFi throughout": Wifi,
  "Linens provided": Home,
  "So many amenities": Heart,
  "Free parking": Car,
  "Ocean views": Waves,
  "Golf cart included": Car,
  "4-seater golf cart included": Car,
};

export default function HomePage() {
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showFloorPlan, setShowFloorPlan] = useState(false);

  const fetchProperty = async () => {
    try {
      const res = await axios.get(`${API}/property`);
      setProperty(res.data);
    } catch (e) {
      console.error("Error fetching property:", e);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => { fetchProperty(); }, 0);
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <SEO 
        title="Historic Maine Coastal Inn"
        description="Experience coastal Maine at Chestnut Hill Inn, a historic circa-1900 seaside inn on Long Island, Maine. Perfect for intimate weddings, family reunions, retreats & Maine island getaways. 7 bedrooms, sleeps 14."
        canonical="/"
      />
      <LodgingSchema />
      {/* Navigation */}
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          scrolled ? "bg-white/95 backdrop-blur-md shadow-sm py-4" : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <a
            href="/"
            data-testid="logo"
            className={`font-serif text-2xl tracking-tight transition-colors ${
              scrolled ? "text-[#1C1917]" : "text-white"
            }`}
          >
            Chestnut Hill Inn
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
            <button
              onClick={() => scrollToSection("about")}
              className={`nav-link text-sm tracking-widest uppercase font-medium transition-colors ${
                scrolled ? "text-[#78716C] hover:text-[#1C1917]" : "text-white/80 hover:text-white"
              }`}
            >
              About
            </button>
            <button
              onClick={() => navigate("/gallery")}
              className={`nav-link text-sm tracking-widest uppercase font-medium transition-colors ${
                scrolled ? "text-[#78716C] hover:text-[#1C1917]" : "text-white/80 hover:text-white"
              }`}
            >
              Pictures
            </button>
            <button
              onClick={() => scrollToSection("rooms")}
              className={`nav-link text-sm tracking-widest uppercase font-medium transition-colors ${
                scrolled ? "text-[#78716C] hover:text-[#1C1917]" : "text-white/80 hover:text-white"
              }`}
            >
              Rooms
            </button>
            <button
              onClick={() => navigate("/reviews")}
              className={`nav-link text-sm tracking-widest uppercase font-medium transition-colors ${
                scrolled ? "text-[#78716C] hover:text-[#1C1917]" : "text-white/80 hover:text-white"
              }`}
            >
              Reviews
            </button>
            <button
              onClick={() => navigate("/winter-retreats")}
              className={`nav-link text-sm tracking-widest uppercase font-medium transition-colors ${
                scrolled ? "text-[#78716C] hover:text-[#1C1917]" : "text-white/80 hover:text-white"
              }`}
            >
              Winter Retreats
            </button>
            <button
              onClick={() => navigate("/faq")}
              className={`nav-link text-sm tracking-widest uppercase font-medium transition-colors ${
                scrolled ? "text-[#78716C] hover:text-[#1C1917]" : "text-white/80 hover:text-white"
              }`}
            >
              FAQ
            </button>
            <button
              onClick={() => navigate("/guest-login")}
              className={`nav-link text-sm tracking-widest uppercase font-medium transition-colors ${
                scrolled ? "text-[#78716C] hover:text-[#1C1917]" : "text-white/80 hover:text-white"
              }`}
            >
              Guest Portal
            </button>
            <Button
              data-testid="nav-book-now"
              onClick={() => navigate("/booking")}
              className={`px-6 py-2 text-xs uppercase tracking-widest font-semibold transition-all ${
                scrolled
                  ? "bg-[#134E4A] text-white hover:bg-[#0f3d3a]"
                  : "bg-white text-[#134E4A] hover:bg-white/90"
              }`}
            >
              Book Now
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden p-2 ${scrolled ? "text-[#1C1917]" : "text-white"}`}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`mobile-menu md:hidden fixed inset-0 top-16 bg-white z-40 p-6 ${
            mobileMenuOpen ? "open" : ""
          }`}
        >
          <div className="flex flex-col gap-6">
            <button
              onClick={() => { scrollToSection("about"); setMobileMenuOpen(false); }}
              className="text-lg font-medium text-[#1C1917] text-left"
            >
              About
            </button>
            <button
              onClick={() => { navigate("/gallery"); setMobileMenuOpen(false); }}
              className="text-lg font-medium text-[#1C1917] text-left"
            >
              Pictures
            </button>
            <button
              onClick={() => { scrollToSection("rooms"); setMobileMenuOpen(false); }}
              className="text-lg font-medium text-[#1C1917] text-left"
            >
              Rooms
            </button>
            <button
              onClick={() => { navigate("/reviews"); setMobileMenuOpen(false); }}
              className="text-lg font-medium text-[#1C1917] text-left"
            >
              Reviews
            </button>
            <button
              onClick={() => { navigate("/faq"); setMobileMenuOpen(false); }}
              className="text-lg font-medium text-[#1C1917] text-left"
            >
              FAQ
            </button>
            <Button
              onClick={() => navigate("/booking")}
              className="bg-[#134E4A] text-white hover:bg-[#0f3d3a] mt-4"
            >
              Book Your Stay
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section data-testid="hero-section" className="relative h-screen">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_bookmate-32/artifacts/dt1aqqgm_20200713182806961693000000-o.jpg"
            alt="Chestnut Hill Inn - Historic Maine Coastal Retreat"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative h-full flex flex-col justify-center items-center text-center px-6">
          <p className="text-white/80 uppercase tracking-[0.3em] text-sm mb-4 animate-fadeIn">
            Long Island, Maine
          </p>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-white tracking-tight leading-[0.9] mb-6 animate-fadeInUp">
            Chestnut Hill
            <br />
            <span className="italic font-normal">Inn</span>
          </h1>
          <p className="text-white/90 text-lg md:text-xl max-w-xl mb-10 animate-fadeInUp animation-delay-200">
            A timeless coastal Maine destination for weddings, gatherings & unforgettable getaways
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp animation-delay-300">
            <Button
              data-testid="hero-book-stay"
              onClick={() => navigate("/booking")}
              className="btn-primary bg-white text-[#134E4A] hover:bg-white/90"
            >
              Book Your Stay
            </Button>
            <Button
              onClick={() => scrollToSection("about")}
              className="btn-secondary border-white text-white hover:bg-white hover:text-[#134E4A]"
            >
              Explore
            </Button>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" data-testid="about-section" className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#D97706] uppercase tracking-[0.3em] text-xs font-semibold mb-4">
                Est. 1900
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#1C1917] tracking-tight mb-6">
                A Timeless
                <br />
                <span className="italic">Retreat</span>
              </h2>
              <div className="w-16 h-px bg-[#134E4A] mb-8" />
              <p className="text-[#78716C] text-lg leading-relaxed mb-6">
                {property?.description?.split("\n\n")[0] ||
                  "Experience the magic of coastal Maine at Chestnut Hill Inn, a lovingly preserved circa-1900 seaside inn on the peaceful west end of Long Island, Maine."}
              </p>
              <p className="text-[#78716C] leading-relaxed mb-6">
                {property?.description?.split("\n\n")[1] ||
                  "Just moments from quiet, secluded Maine beaches—including South Beach, Fowler Beach, and Wreck Cove—guests can wander between rocky shorelines and soft sandy beaches."}
              </p>
              <p className="text-[#78716C] leading-relaxed">
                {property?.description?.split("\n\n")[2] ||
                  "Chestnut Hill Inn is an ideal destination for intimate coastal weddings in Maine, private retreats, family reunions, and unforgettable Maine island getaways."}
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_bookmate-32/artifacts/u1pldd43_20200713182819349145000000-o.jpg"
                  alt="Chestnut Hill Inn Porch"
                  className="w-full h-full object-cover img-zoom"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-[#134E4A] text-white p-8 max-w-xs hidden md:block">
                <p className="font-serif text-3xl mb-1">{property?.bedrooms || 7}</p>
                <p className="text-sm uppercase tracking-widest opacity-80">
                  Bedrooms
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#134E4A] py-12 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { value: property?.bedrooms || 7, label: "Bedrooms" },
            { value: property?.bathrooms || 6, label: "Bathrooms" },
            { value: property?.max_guests || 14, label: "Guests" },
            { value: "1900", label: "Established" },
          ].map((stat, i) => (
            <div key={i}>
              <p className="font-serif text-4xl md:text-5xl text-white mb-2">{stat.value}</p>
              <p className="text-white/60 text-sm uppercase tracking-widest">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Rooms Section */}
      <section id="rooms" data-testid="rooms-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#F5F5F4]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#D97706] uppercase tracking-[0.3em] text-xs font-semibold mb-4">
              Accommodations
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1C1917] tracking-tight">
              Relaxed Island <span className="italic">Comfort</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="gallery-item group">
              <div className="aspect-[4/5] overflow-hidden mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_bookmate-32/artifacts/vvgiogzr_20200713182836094332000000-o%20%281%29.jpg"
                  alt="Dining and Living Area"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-serif text-2xl text-[#1C1917] mb-2">Comfortable Spaces</h3>
              <p className="text-[#78716C]">
                Spacious dining and living areas with cathedral ceilings, perfect for group meals and celebrations.
              </p>
            </div>
            <div className="gallery-item group mt-0 md:mt-16">
              <div className="aspect-[4/5] overflow-hidden mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_bookmate-32/artifacts/dfit3vnd_20200713182833135775000000-o.jpg"
                  alt="Cozy Sitting Area"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-serif text-2xl text-[#1C1917] mb-2">Cozy Retreats</h3>
              <p className="text-[#78716C]">
                Charming sitting areas throughout the inn, with turn of the century details.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Events Banner */}
      <section className="relative h-[70vh]">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_bookmate-32/artifacts/mnrnnulb_20200713182810432397000000-o.jpg"
            alt="Chestnut Hill Inn Exterior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="relative h-full flex flex-col justify-center items-center text-center px-6">
          <p className="text-white/80 uppercase tracking-[0.3em] text-sm mb-4">
            Gatherings
          </p>
          <h2 className="font-serif text-4xl md:text-6xl text-white tracking-tight mb-6">
            Perfect for <span className="italic">Celebrations</span>
          </h2>
          <p className="text-white/80 max-w-lg mb-8">
            Host your special occasions amid classic Maine charm, wrap-around porches, and the rare joy of gathering all your guests under one roof.
          </p>
          <Button
            onClick={() => navigate("/booking")}
            className="btn-primary bg-white text-[#134E4A] hover:bg-white/90"
          >
            Inquire Now
          </Button>
        </div>
      </section>

      {/* Amenities Section */}
      <section id="amenities" data-testid="amenities-section" className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div className="md:sticky md:top-32">
              <p className="text-[#D97706] uppercase tracking-[0.3em] text-xs font-semibold mb-4">
                Features
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#1C1917] tracking-tight mb-6">
                Everything You Need for a <span className="italic">Relaxing Island Getaway</span>
              </h2>
              <p className="text-[#78716C] leading-relaxed mb-8">
                Fully equipped kitchens, spacious rooms, and just minutes from three stunning beaches—every detail is designed to make your stay comfortable and memorable.
              </p>
              <div className="flex items-center gap-4">
                <div className="text-[#134E4A]">
                  <MapPin size={20} />
                </div>
                <p className="text-[#78716C]">{property?.address || "209 Beach Ave, Long Island, ME"}</p>
              </div>
              
              {/* Bedroom Layout Button - Enhanced */}
              <div className="mt-8 p-6 bg-gradient-to-r from-[#134E4A] to-[#1a6b65] rounded-lg shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="text-white">
                    <h4 className="font-serif text-xl mb-1">7 Bedrooms • Sleeps 14</h4>
                    <p className="text-white/70 text-sm">4 Queen Suites with Private Baths</p>
                  </div>
                  <Button
                    onClick={() => setShowFloorPlan(true)}
                    className="bg-white text-[#134E4A] hover:bg-stone-100 flex items-center gap-2 px-6 py-3 text-base font-semibold shadow-md"
                    data-testid="bedroom-layout-btn"
                  >
                    <BedDouble size={22} />
                    View Upstairs Floor Plan
                  </Button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {(property?.amenities || [
                "Steps to sandy beaches",
                "Full commercial kitchen",
                "Free parking",
                "Ferry shuttle service",
                "Ocean views",
                "Private ensuites",
                "Golf cart included",
                "WiFi throughout",
              ]).map((amenity, i) => {
                const IconComponent = amenityIcons[amenity] || Home;
                return (
                  <div
                    key={i}
                    className="feature-card bg-white border border-stone-100 p-6 card-hover"
                  >
                    <div className="feature-icon w-12 h-12 bg-[#e6f2f1] text-[#134E4A] flex items-center justify-center mb-4">
                      <IconComponent size={20} />
                    </div>
                    <p className="text-[#1C1917] font-medium">{amenity}</p>
                    {amenity === "Golf cart included" && (
                      <p className="text-xs text-[#78716C] mt-1">Max 20 mph on Long Island</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section id="location" data-testid="location-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#F5F5F4]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#D97706] uppercase tracking-[0.3em] text-xs font-semibold mb-4">
              Getting Here
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1C1917] tracking-tight">
              Island <span className="italic">Escape</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Ship,
                title: "Ferry Access",
                desc: "Access the island via Casco Bay Lines — a scenic ferry ride just six miles from Portland.",
                link: "https://www.cascobaylines.com/",
              },
              {
                icon: Car,
                title: "From Portland",
                desc: "Enjoy the scenic 45-minute ferry ride over. Your stay includes a 4-seater golf cart, or inquire about a shuttle from the pier to the house (approx 0.8 mile).",
              },
              {
                icon: Waves,
                title: "Beach Steps",
                desc: "Step outside and stroll to peaceful, serene beaches and classic Maine coastline.",
              },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-[#134E4A] text-white mx-auto mb-6 flex items-center justify-center">
                  <item.icon size={24} />
                </div>
                <h3 className="font-serif text-xl text-[#1C1917] mb-3">{item.title}</h3>
                <p className="text-[#78716C]">
                  {item.link ? (
                    <>
                      Access the island via{" "}
                      <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-[#134E4A] underline hover:text-[#0f3d3a]">
                        Casco Bay Lines
                      </a>
                      {" "}— a scenic ferry ride just six miles from Portland.
                    </>
                  ) : (
                    item.desc
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#134E4A]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#D97706] uppercase tracking-[0.3em] text-sm font-semibold mb-4">
            Book Your Escape
          </p>
          <h2 className="font-serif text-4xl md:text-6xl text-white tracking-tight mb-6">
            Starting at <span className="italic">${property?.base_price_per_night || 800}</span>
            <span className="text-2xl font-sans opacity-60">/night</span>
          </h2>
          <p className="text-white/70 max-w-lg mx-auto mb-10">
            Exclusive whole-home rental for up to {property?.max_guests || 14} guests. Perfect for your next celebration or getaway.
          </p>
          <Button
            data-testid="cta-check-availability"
            onClick={() => navigate("/booking")}
            className="btn-primary bg-white text-[#134E4A] hover:bg-white/90 text-sm"
          >
            Check Availability
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 md:px-12 border-t border-stone-200">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <h3 className="font-serif text-2xl text-[#1C1917] mb-4">Chestnut Hill Inn</h3>
              <p className="text-[#78716C] mb-6 max-w-sm">
                A historic Maine coastal retreat for weddings, family gatherings, and unforgettable getaways.
              </p>
              <div className="space-y-2">
                <p className="text-[#78716C] text-sm">
                  209 Beach Ave, Long Island, ME
                </p>
                <p className="text-[#78716C] text-sm">
                  <a href="mailto:chestnuthillinn@gmail.com" className="hover:text-[#134E4A] transition-colors">
                    chestnuthillinn@gmail.com
                  </a>
                </p>
                <p className="text-[#78716C] text-sm">
                  <a href="tel:603-858-2272" className="hover:text-[#134E4A] transition-colors">
                    603-858-2272
                  </a>
                </p>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-widest text-[#1C1917] font-semibold mb-4">
                Quick Links
              </h4>
              <div className="flex flex-col gap-3">
                {["About", "Rooms", "Amenities", "Location"].map((item) => (
                  <button
                    key={item}
                    onClick={() => scrollToSection(item.toLowerCase())}
                    className="text-[#78716C] hover:text-[#134E4A] text-left transition-colors"
                  >
                    {item}
                  </button>
                ))}
                <button
                  onClick={() => navigate("/faq")}
                  className="text-[#78716C] hover:text-[#134E4A] text-left transition-colors"
                >
                  FAQ
                </button>
                <button
                  onClick={() => navigate("/winter-retreats")}
                  className="text-[#78716C] hover:text-[#134E4A] text-left transition-colors"
                >
                  Winter Retreats
                </button>
                <button
                  onClick={() => navigate("/rental-agreement")}
                  className="text-[#78716C] hover:text-[#134E4A] text-left transition-colors"
                >
                  Rental Agreement
                </button>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-widest text-[#1C1917] font-semibold mb-4">
                Contact Us
              </h4>
              <div className="flex flex-col gap-3">
                <a href="mailto:chestnuthillinn@gmail.com" className="text-[#78716C] hover:text-[#134E4A] transition-colors">
                  Email Us
                </a>
                <a href="tel:603-858-2272" className="text-[#78716C] hover:text-[#134E4A] transition-colors">
                  Call: 603-858-2272
                </a>
                <button
                  onClick={() => navigate("/booking")}
                  className="text-[#78716C] hover:text-[#134E4A] text-left transition-colors"
                >
                  Book Now
                </button>
                <a
                  href="/admin"
                  className="text-[#78716C] hover:text-[#134E4A] transition-colors"
                >
                  Admin Login
                </a>
              </div>
              {/* Social Media Links */}
              <div className="mt-6">
                <h5 className="text-sm uppercase tracking-widest text-[#1C1917] font-semibold mb-3">
                  Follow Us
                </h5>
                <div className="flex gap-4">
                  <a
                    href="https://www.facebook.com/chestnut.hill.inn.and.retreat.center"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#78716C] hover:text-[#134E4A] transition-colors"
                    aria-label="Facebook"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                    </svg>
                  </a>
                  <a
                    href="https://www.instagram.com/chestnuthillinn/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#78716C] hover:text-[#134E4A] transition-colors"
                    aria-label="Instagram"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-stone-200 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#78716C] text-sm">
              © {new Date().getFullYear()} Chestnut Hill Inn. All rights reserved.
            </p>
            <p className="text-[#78716C] text-sm">
              Long Island, Maine
            </p>
          </div>
        </div>
      </footer>

      {/* Floor Plan Modal */}
      {showFloorPlan && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setShowFloorPlan(false)}
        >
          <div 
            className="relative bg-white rounded-lg max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowFloorPlan(false)}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-stone-100 transition-colors"
              data-testid="close-floor-plan-modal"
            >
              <X size={24} className="text-[#1C1917]" />
            </button>
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-[#e6f2f1] rounded-full flex items-center justify-center mx-auto mb-4">
                <BedDouble size={32} className="text-[#134E4A]" />
              </div>
              <h3 className="font-serif text-2xl text-[#1C1917] mb-2">Upstairs Bedroom Layout</h3>
              <p className="text-[#78716C] mb-6">7 Bedrooms • 4 Queen Suites • 5 Bathrooms</p>
              
              <div className="bg-stone-50 rounded-lg p-6 mb-6">
                <p className="text-[#78716C] mb-4">
                  View our detailed floor plan showing all bedroom configurations, bathroom locations, and common areas.
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm text-left max-w-md mx-auto">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-[#134E4A] rounded-full"></div>
                    <span>4 Queen Suites with private baths</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-[#134E4A] rounded-full"></div>
                    <span>3 Additional bedrooms and futon room</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-[#134E4A] rounded-full"></div>
                    <span>5 Full bathrooms</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-[#134E4A] rounded-full"></div>
                    <span>Sleeps up to 14 guests</span>
                  </div>
                </div>
              </div>
              
              <a
                href={FLOOR_PLAN_IMAGE}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#134E4A] text-white px-6 py-3 rounded-lg hover:bg-[#0f3d3a] transition-colors"
              >
                <BedDouble size={18} />
                View Full Floor Plan
              </a>
              <p className="text-xs text-[#78716C] mt-3">Opens PDF in a new tab</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
